/**
 * Обёртка над ECharts.
 *
 * Собирается из отдельных модулей, а не из `echarts/index`: полный пакет тянет
 * весь набор типов диаграмм, из которых здесь нужны два.
 */

import { useEffect, useRef } from "react";
import * as echarts from "echarts/core";
import { BarChart, LineChart } from "echarts/charts";
import { GridComponent, LegendComponent, TooltipComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import type { EChartsOption } from "echarts";

echarts.use([BarChart, LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

export function Chart({ option, height = 260 }: { option: EChartsOption; height?: number }) {
  const holder = useRef<HTMLDivElement>(null);
  const chart = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!holder.current) return;
    chart.current = echarts.init(holder.current, undefined, { renderer: "canvas" });
    const observer = new ResizeObserver(() => chart.current?.resize());
    observer.observe(holder.current);
    return () => {
      observer.disconnect();
      chart.current?.dispose();
      chart.current = null;
    };
  }, []);

  useEffect(() => {
    // notMerge: иначе смена фильтра оставляет на графике серии от прошлых
    // данных, и на экране оказывается смесь двух ответов.
    chart.current?.setOption(option, { notMerge: true });
  }, [option]);

  return <div ref={holder} style={{ height, width: "100%" }} />;
}

/** Общая часть настроек: сетка, подсказка, цвета из переменных темы. */
export function baseOption(): EChartsOption {
  const style = getComputedStyle(document.documentElement);
  const text = style.getPropertyValue("--text-muted").trim() || "#64748b";
  const line = style.getPropertyValue("--border").trim() || "#e2e8f0";
  return {
    grid: { left: 8, right: 16, top: 24, bottom: 8, containLabel: true },
    tooltip: { trigger: "axis", confine: true },
    textStyle: { fontFamily: "inherit", color: text },
    xAxis: { axisLine: { lineStyle: { color: line } }, axisTick: { show: false } },
    yAxis: { splitLine: { lineStyle: { color: line } } },
  };
}

export const SERIES_COLOR = "#2563eb";
export const SECOND_COLOR = "#0d9488";
